import React, {useContext} from 'react';

import {DateInput} from 'src/components/input';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton, SkeletonItem} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useConfig} from 'src/modules/config';

import {CheckBox} from '@pb/kit-component-checkbox';
import {FormSection} from '@pb/kit-component-form-section';
import {Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import {CustomGroupItem} from '@pb/kit-component-input-v2';
import {LocaleContext} from '@pb/kit-context-locale';

import {AiTokenLimitItem} from '../components/ai-token-limit-item';
import {ResetUserDefaultLimits} from '../components/reset-user-default-limits';
import {AI_TOKEN_LABELS, AI_TOKEN_PERIODS, toAiTokenExpiry, toAiTokenUntil} from '../utils/ai-token-limits';

import type {useUserAiTokenLimits} from '../hooks/useUserAiTokenLimits';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {AiTokenLimitPeriod} from 'src/openapi/admin/models/AiTokenLimitPeriod';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    limits: ReturnType<typeof useUserAiTokenLimits>,
    disabled?: boolean,
    onFieldChange: (name: string) => void,
};

export const UserAiTokenLimitsView = ({limits, disabled, onFieldChange}: Props) => {
    const {
        data, overrides, setOverrides, temporary, setTemporary, setUntil, clearTemporaryLimit,
        isLoading, loadingError, resetLimits,
    } = limits;
    const {today, getTodayString} = useDateTime();
    const {timeZone} = useConfig();
    const {locale} = useContext(LocaleContext);
    const headline = 'PitchBook AI Token Limits';
    const fieldChanged = (field: string) => onFieldChange(`PitchBook AI: ${field}`);
    const isTemporary = !!temporary.until;
    const isDefaultUsageLimits = !overrides.length && !isTemporary && !temporary.limits.length;

    const handleReset = () => {
        fieldChanged('Reset to defaults');
        resetLimits();
    };

    const handleTemporaryChange = ({event}: CheckBoxChangeData) => {
        fieldChanged('Make changes temporary');
        const until = event.currentTarget.checked ? getTodayString() : '';
        setTemporary({until, limits: []});
    };

    const handleUntilChange = (until: string) => {
        if (until === temporary.until) {
            return;
        }
        fieldChanged('Until');
        setUntil(until);
    };

    const renderLimit = (period: AiTokenLimitPeriod) => {
        const defaultLimit = data.defaults.find(limit => limit.period === period);
        if (!defaultLimit) {
            return null;
        }
        const tempLimit = temporary.limits.find(limit => limit.period === period);
        const customValue = overrides.find(limit => limit.period === period)?.amount;
        const usedValue = data.limits.find(limit => limit.period === period)?.usedAmount;
        const until = tempLimit ? toAiTokenUntil(tempLimit.expiresOn, timeZone) : undefined;

        const handleAmountChange = (amount: number) => {
            const limitType = isTemporary ? 'Temporary' : 'Custom';
            fieldChanged(`${limitType} ${period}`);
            if (isTemporary) {
                setTemporary(previous => {
                    const otherLimits = previous.limits.filter(limit => limit.period !== period);
                    const expiresOn = tempLimit?.expiresOn || toAiTokenExpiry(previous.until, timeZone);
                    return {...previous, limits: [...otherLimits, {period, amount, expiresOn}]};
                });
            } else {
                setOverrides(previous => {
                    const otherLimits = previous.filter(limit => limit.period !== period);
                    const updatedLimit = amount === defaultLimit.amount ? [] : [{period, amount}];
                    return [...otherLimits, ...updatedLimit];
                });
            }
        };

        const handleClearCustom = () => {
            fieldChanged(`Clear custom ${period}`);
            setOverrides(previous => previous.filter(limit => limit.period !== period));
        };

        const handleClearTemporary = () => {
            fieldChanged(`Clear temporary ${period}`);
            clearTemporaryLimit(period);
        };

        return (
            <WidthItem key={period} width={300}>
                <AiTokenLimitItem
                    period={period}
                    defaultValue={defaultLimit.amount}
                    customValue={customValue}
                    temporaryValue={tempLimit?.amount}
                    usedValue={usedValue}
                    until={until}
                    disabled={disabled}
                    onChange={handleAmountChange}
                    onClearCustom={handleClearCustom}
                    onClearTemporary={handleClearTemporary}
                />
            </WidthItem>
        );
    };

    const defaultLimitsContent = (
        <Group direction="v" gap="s">
            <b>PitchBook AI defaults</b>
            {data.defaults.map(limit => (
                <Group key={limit.period} gap="s">
                    <span>{AI_TOKEN_LABELS[limit.period]}:</span>
                    <b>{Integer.format(limit.amount, locale)}</b>
                </Group>
            ))}
        </Group>
    );

    return (
        <Skeleton
            shown={isLoading}
            view={(
                <FormSection headline={headline}>
                    <Group direction="v">
                        <SkeletonItem width={400} height={32}/>
                        <SkeletonItem width={400} height={32}/>
                        <Group><SkeletonItem width={300} height={32}/><SkeletonItem width={300} height={32}/></Group>
                    </Group>
                </FormSection>
            )}>
            <ServerResponseHandler
                serverError={loadingError}
                smallView={true}
                accessRight={ACCESS_RIGHTS.USER_LIMITS_VIEW as AccessRight}>
                <FormSection headline={headline}>
                    <Group direction="v">
                        <ResetUserDefaultLimits
                            defaultsLabel="PitchBook AI"
                            defaultUsageLimits={null}
                            defaultLimitsContent={defaultLimitsContent}
                            isDefaultUsageLimits={isDefaultUsageLimits}
                            loading={isLoading}
                            disabled={disabled}
                            onReset={handleReset}
                        />
                        <Group>
                            <CheckBox
                                label="Make changes temporary"
                                disabled={disabled}
                                checked={isTemporary}
                                onCheckBoxChange={handleTemporaryChange}
                            />
                            <CustomGroupItem label="Until">
                                <DateInput
                                    id="ai-token-limits-until"
                                    clearable={true}
                                    width={270}
                                    selectedDate={temporary.until}
                                    minDate={today}
                                    readonly={disabled}
                                    onChange={handleUntilChange}
                                />
                            </CustomGroupItem>
                        </Group>
                        <ResponsiveElementGroup>
                            {AI_TOKEN_PERIODS.map(renderLimit)}
                        </ResponsiveElementGroup>
                    </Group>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
